import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Download, X, ImageSquare } from "@phosphor-icons/react";
import Brand from "@/components/Brand";

/**
 * ReferralPoster — Instagram-Story-sized (1080×1920) shareable poster
 * for a candidate's referral link. Rendered off-screen inside the DOM,
 * then rasterised to PNG via html2canvas and downloaded.
 *
 * V1.1 #8. Only the "candidate referral" variant ships now; the
 * "recruiter partner" variant will land with V1.1 #11 (Partner Landing).
 */
export default function ReferralPoster({
    open,
    onClose,
    referralCode,
    referrerName,
    bountyLabel,       // e.g. "₹1,000"
    shareUrl,          // full referral URL (already includes /api/r/{code})
    onDownloaded,      // optional callback (analytics)
}) {
    const canvasRef = useRef(null);
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [downloading, setDownloading] = useState(false);

    // Generate QR encoding the referral URL with `?src=poster` attribution
    useEffect(() => {
        if (!open || !shareUrl) return;
        const posterUrl = shareUrl + (shareUrl.includes("?") ? "&" : "?") + "src=poster";
        QRCode.toDataURL(posterUrl, {
            width: 640,
            margin: 2,
            errorCorrectionLevel: "H",
            color: { dark: "#0A0A0A", light: "#FFFFFF" },
        })
            .then(setQrDataUrl)
            .catch(() => setQrDataUrl(""));
    }, [open, shareUrl]);

    const download = async () => {
        if (!canvasRef.current) return;
        setDownloading(true);
        try {
            const canvas = await html2canvas(canvasRef.current, {
                backgroundColor: "#0A0A0A",
                scale: 1,
                useCORS: true,
                logging: false,
            });
            const link = document.createElement("a");
            link.download = `naukridaddy-referral-${referralCode}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            toast.success("Poster downloaded. Share it on Instagram Story, WhatsApp Status, or Facebook.");
            if (onDownloaded) onDownloaded();
        } catch (e) {
            toast.error("Couldn't generate poster. Try again in a moment.");
        } finally {
            setDownloading(false);
        }
    };

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="referral-poster-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-[#E5E5E5] w-full max-w-md max-h-[92vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5]">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-1">
                            <ImageSquare size={11} weight="fill" className="inline mr-1" /> Downloadable poster
                        </p>
                        <h2 className="font-heading text-xl font-black tracking-tighter">
                            Share your referral card
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="referral-poster-close"
                        className="p-2 hover:bg-[#F5F5F5]"
                        aria-label="Close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="p-5">
                    {/* Preview — scaled down to fit modal, then rasterised at full 1080×1920 */}
                    <div
                        className="mx-auto border border-[#E5E5E5] overflow-hidden"
                        style={{ width: "270px", height: "480px" }}
                    >
                        <div
                            ref={canvasRef}
                            data-testid="referral-poster-canvas"
                            style={{
                                width: "1080px",
                                height: "1920px",
                                transform: "scale(0.25)",
                                transformOrigin: "top left",
                                background: "linear-gradient(180deg,#0A0A0A 0%,#111827 60%,#1E293B 100%)",
                                color: "#FFFFFF",
                                position: "relative",
                                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
                            }}
                        >
                            {/* Header */}
                            <div style={{ padding: "80px 80px 0 80px" }}>
                                <div style={{
                                    display: "inline-block",
                                    fontSize: "22px",
                                    fontWeight: 900,
                                    letterSpacing: "0.35em",
                                    textTransform: "uppercase",
                                    color: "#2447F5",
                                    marginBottom: "48px",
                                }}>
                                    NAUKRIDADDY · REFER &amp; EARN
                                </div>
                                <div style={{
                                    fontSize: "88px",
                                    fontWeight: 900,
                                    letterSpacing: "-0.03em",
                                    lineHeight: 1.02,
                                    marginBottom: "32px",
                                }}>
                                    I earn <span style={{ color: "#2447F5" }}>{bountyLabel}</span>
                                    <br />when you get hired.
                                </div>
                                <p style={{
                                    fontSize: "30px",
                                    lineHeight: 1.5,
                                    color: "rgba(255,255,255,0.75)",
                                    marginBottom: "72px",
                                    maxWidth: "780px",
                                }}>
                                    Sign up on NaukriDaddy with my code and land your
                                    next role through India's most curated agency job board.
                                </p>
                            </div>

                            {/* Code + QR block */}
                            <div style={{
                                margin: "0 80px",
                                background: "#FFFFFF",
                                color: "#0A0A0A",
                                padding: "56px 60px",
                                display: "flex",
                                gap: "48px",
                                alignItems: "center",
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{
                                        fontSize: "20px",
                                        fontWeight: 900,
                                        letterSpacing: "0.3em",
                                        textTransform: "uppercase",
                                        color: "#2447F5",
                                        marginBottom: "12px",
                                    }}>
                                        Referral code
                                    </p>
                                    <p style={{
                                        fontSize: "72px",
                                        fontWeight: 900,
                                        letterSpacing: "-0.04em",
                                        color: "#0A0A0A",
                                        lineHeight: 1,
                                        marginBottom: "22px",
                                    }}>
                                        {referralCode}
                                    </p>
                                    <p style={{
                                        fontSize: "24px",
                                        color: "#525252",
                                        fontWeight: 700,
                                    }}>
                                        Scan or visit
                                        <br />
                                        naukridaddy.com
                                    </p>
                                </div>
                                {qrDataUrl && (
                                    <img
                                        src={qrDataUrl}
                                        alt="Referral QR"
                                        style={{ width: "280px", height: "280px", flexShrink: 0 }}
                                    />
                                )}
                            </div>

                            {/* Footer */}
                            <div style={{
                                position: "absolute",
                                bottom: 80,
                                left: 80,
                                right: 80,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                            }}>
                                <div>
                                    <p style={{
                                        fontSize: "18px",
                                        fontWeight: 900,
                                        letterSpacing: "0.25em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.55)",
                                        marginBottom: "8px",
                                    }}>
                                        Shared by
                                    </p>
                                    <p style={{
                                        fontSize: "36px",
                                        fontWeight: 900,
                                        color: "#FFFFFF",
                                    }}>
                                        {referrerName || "A NaukriDaddy member"}
                                    </p>
                                </div>
                                <div>
                                    <div style={{
                                        display: "inline-block",
                                        fontFamily: "'Bebas Neue',Impact,sans-serif",
                                        fontSize: "36px",
                                        fontWeight: 900,
                                        letterSpacing: "0.15em",
                                        color: "#FFFFFF",
                                    }}>
                                        NAUKRIDADDY
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="text-xs text-[#525252] text-center mt-4 leading-relaxed">
                        Preview at 25%. The downloaded PNG is 1080×1920 —<br />
                        perfect for Instagram Story, WhatsApp Status &amp; Facebook.
                    </p>

                    <button
                        onClick={download}
                        disabled={downloading || !qrDataUrl}
                        data-testid="referral-poster-download"
                        className="w-full mt-5 bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm inline-flex items-center justify-center gap-2"
                    >
                        {downloading ? "Generating…" : (
                            <>
                                <Download size={14} weight="bold" /> Download poster (PNG)
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
